// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Legacy code
import { action, makeObservable, observable, when } from 'mobx';

import { getOrCreateApiGateway } from '../server/apiGateway';
import { type AuthStore, getOrCreateAuthStore } from './authStore';
import { NavinaCache } from './cache';

const VALUES_DICTIONARY_LOCAL_STORAGE_KEY = 'UserConfig';

interface ValuesDictionaryType {
	readonly [key: string]: any;
}

export class UserConfigStore {
	valuesDictionary: ValuesDictionaryType = {};

	private readonly authStore: AuthStore;
	private isLoadingStarted = false;

	constructor(authStore: AuthStore) {
		makeObservable(this, {
			valuesDictionary: observable,
			setValuesDictionary: action,
			setValue: action,
			parseUserConfig: action,
		});

		this.authStore = authStore;
	}

	// The user config only backs the add-on new-window/new-tab preference, so it must not gate the
	// initial app load. Callers trigger this lazily (e.g. when the settings UI is rendered) and it
	// fetches at most once per session.
	readonly startLoadingUserConfig = (): void => {
		if (this.isLoadingStarted) {
			return;
		}

		this.isLoadingStarted = true;

		const authStore = this.authStore;

		// `when` fires the effect exactly once - the first time a valid token is observed - and then
		// disposes itself, so a later token refresh/re-auth does not trigger another getUserConfig call.
		when(
			(): boolean => Boolean(authStore.getToken) && !window.location.pathname.includes('epic/login'),
			(): void => this.fetchUserConfigFromCacheThenApi(),
		);
	};

	private readonly fetchUserConfigFromCacheThenApi = (): void => {
		const userConfigStr = NavinaCache.get(VALUES_DICTIONARY_LOCAL_STORAGE_KEY);
		if (userConfigStr) {
			this.parseUserConfig(userConfigStr);
		}

		getOrCreateApiGateway()
			.getUserConfig()
			.then((userConfigResponse): void => {
				if (userConfigResponse?.data?.result) {
					this.setValuesDictionary(userConfigResponse?.data?.result);
					NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(this.valuesDictionary));
				} else {
					console.warn('Unable to retrieve user config', userConfigResponse);
				}
			})
			.catch((err): void => console.warn('Error retrieving user config', err));
	};

	readonly setValuesDictionary = (valuesDictionary: typeof this.valuesDictionary): void => {
		this.valuesDictionary = valuesDictionary;
	};

	// Rename to a descriptive name
	readonly setValuesDictionaryAndCallback = (
		valuesDictionary: ValuesDictionaryType,
		callback?: (isSuccess: boolean, errorMessage?: string) => void,
	): void => {
		const oldValues = { ...this.valuesDictionary };

		this.setValuesDictionary(valuesDictionary);

		NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(valuesDictionary));

		getOrCreateApiGateway()
			.updateUserConfig(valuesDictionary)
			.then((response): void => {
				callback?.(Boolean(response?.data?.result));
			})
			.catch((error): void => {
				console.warn('Unable to set user config', error);
				callback?.(false, error.message || error);
				this.setValuesDictionary(oldValues);
				NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(oldValues));
			});
	};

	readonly setValue = (
		key: string,
		value: any,
		callback?: (isSuccess: boolean, errorMessage?: string) => void,
	): void => {
		const newValuesDictionary = { ...this.valuesDictionary };

		newValuesDictionary[key] = value;

		this.setValuesDictionaryAndCallback(newValuesDictionary, callback);
	};

	readonly parseUserConfig = (userConfigString: string): void => {
		try {
			if (userConfigString) {
				this.valuesDictionary = JSON.parse(userConfigString);
			}
		} catch (error) {
			console.error('Error parsing user config', { userConfigString, error });
		}
	};
}

let maybeUserConfigStore: UserConfigStore | null = null;

// Created lazily on first use (only reached from the user-settings panel), so the store and its
// backing getUserConfig request never touch the app's initial load path.
export function getOrCreateUserConfigStore(): UserConfigStore {
	maybeUserConfigStore ??= new UserConfigStore(getOrCreateAuthStore());

	return maybeUserConfigStore;
}
